import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { useTitanApp } from "@/hooks/useTitanApp";

import { Template } from "@/lib/settings/templatesData";
import { MessageActionType } from "@/enum/MessageActionType";

import {
  getClinicalServiceBuilderResponse,
  addNewServiceType,
  updateNewServiceType,
  deleteNewService,
  updateServiceSorting,
  removeServiceFromPharmacy,
} from "@/dispatchers/typeDispatchers";
import {
  getDefaultCompanyServices,
  getDefaultCompanyCheckList,
  getQRServiceCheckList,
  duplicateService,
} from "@/dispatchers/pharmacyServiceDispatchers";
import { getServiceQRCheckListData } from "@/features/pharmacyServiceSlice/pharmacyServiceSlice";
import { dispatch } from "@/store/store";
import { getEmailsAndSmsTemplates } from "@/dispatchers/templateDispatchers";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import { showError } from "@/components/ToastedMessage/ToastedMessage";
import { nhsAhtWeightTemplateId } from "@/data/constantStrings";

export interface ServiceQRCodePanelProps {
  service: Service;
  onBack: () => void;
}

export interface SetupCheck {
  id: string;
  label: string;
  description: string;
  passed: boolean;
  settingsTab: string;
}

interface TimeBlock {
  from: string;
  to: string;
}

interface DaySchedule {
  enabled: boolean;
  timeBlocks: TimeBlock[];
}

export interface ServiceSchedule {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

export interface Service {
  _id: string;
  name: string;
  price: number;
  duration: number;
  color: string;
  showOnPublicCalendar: boolean;
  roomRequired: boolean;
  enablePrePayments: boolean;
  emailConfirmationTemplate: string;
  emailCancellationTemplate: string;
  smsConfirmationTemplate: string;
  smsCancellationTemplate: string;
  emailReminderTemplate: string;
  smsReminderTemplate: string;
  preConsultTemplate: string;
  defaultConsultationTemplate: string;
  schedule: ServiceSchedule;
  active: boolean;
  serviceType: number;
  serviceCode: number;
  assignedRoomIds?: string[];
  assignedPharmacistIds?: string[];
  emailRescheduleTemplate?: string;
  smsRescheduleTemplate?: string;
  allowGroupBookings?: boolean;
  maxGroupSize?: number;
  serviceChargesType?: number;
  sortingNumber?: number;
  assignedLocationIds?: string[];
  assignedPharmacistsByLocation?: { [locationId: string]: string[] };
  scheduleByLocation?: { [locationId: string]: ServiceSchedule };
  assignedDatesForLocations?: {
  [locationId: string]: {
    serviceStartDate: string;
    serviceEndDate: string;
  };
};
}

export interface ServiceFormPanelProps {
  service?: Service | null;
  defaultService?: Service | null;
  templates: Template[];
  isDarkMode?: boolean;
  onSave: (serviceData: Service) => void;
  onBack: () => void;
}

export interface ServicesSettingsTabProps {
  isDarkMode?: boolean;
  onAddService: () => void;
  onEditService: (service: Service) => void;
  onShowQRCode: (service: Service) => void;
  confirmDelete: (onConfirm: () => void, itemType?: string) => void;
}
export interface ServicesDataProps {
  initialService?: Service;
  onBack?: () => void;
  onSave?: (serviceData: Service) => void;
}

interface DefaultCompanyChecklist {
  serviceCount?: number;
  userCount?: number;
  company?: Record<string, any> | null;
  activeLocationsCount?: number;
  roomsCount?: number;
  allServicesConfigured?: boolean;
}

export const useServiceSettingsData = ({
  initialService,
  onBack,
  onSave,
}: ServicesDataProps = {}) => {
  const {
    appState,
    pharmacyServiceState,
    memberState,
    templateState,
    locationState,
    marketAppState,
  } = useTitanApp();
  const { darkMode, companyId, currentPharmacyId, currentUser, features } =
    appState || {};

  const {
    defaultPharmacyServices: pharmacyServicesFromPharmacyState,
    isPharmacyServiceLoading: isPharmacyLoadingFromPharmacyState,
    roomsData,
  } = pharmacyServiceState || {};
  const {
    defaultPharmacyServices: pharmacyServicesFromMemberState,
    isPharmacyServiceLoading: isPharmacyLoadingFromMemberState,
    membersData,
  } = memberState || {};

  const {
    emailsAndSmsTemplates,
    currentPreConsultTemplates,
    currentAiTemplates,
    defaultCompanyMessageTemplates,
  } = templateState || {};
  const isDarkMode = darkMode ?? false;
  const { allPharmacies } = locationState || {};
  const { companyMarketplaceAppsData } = marketAppState || {};
  const { ifAHTWeightManagementEnabled } = features || {};

  const currentPharmacyOds = useMemo(() => {
    const matched = (allPharmacies || []).find(
      (p: any) => (p?.teamPharmacyID || p?.pharmacyID) === currentPharmacyId,
    );
    return matched?.odsCode || matched?.ODSCode || matched?.ods || "";
  }, [allPharmacies, currentPharmacyId]);

  const isAhtWeightManagementInstalled = useMemo(
    () =>
      ifAHTWeightManagementEnabled &&
      companyMarketplaceAppsData?.some(
        (app: any) => app.marketType === 5 && app.ods === currentPharmacyOds,
      ),
    [ifAHTWeightManagementEnabled, companyMarketplaceAppsData, currentPharmacyOds],
  );

  // Helper function to get default template for specific action and type
  const getDefaultTemplateForAction = (
    actionType: number,
    templateType: "email" | "sms",
  ): string => {
    if (
      !defaultCompanyMessageTemplates ||
      !Array.isArray(defaultCompanyMessageTemplates)
    ) {
      return "";
    }

    const typeMap: Record<"email" | "sms", number> = { email: 0, sms: 1 };
    const template = defaultCompanyMessageTemplates.find(
      (t: any) =>
        Number(t.actionType) === Number(actionType) &&
        Number(t.messageType) === typeMap[templateType],
    );

    return template?.templateName || "";
  };

  const defaultService: Service = {
    _id: "",
    name: "",
    price: 0.0,
    duration: 30,
    color: "#3b82f6",
    assignedDatesForLocations: {},
    showOnPublicCalendar: true,
    roomRequired: false,
    enablePrePayments: false,
    // Set default templates for all fields
    emailConfirmationTemplate: getDefaultTemplateForAction(
      MessageActionType.EmailConfirmation,
      "email",
    ),
    emailCancellationTemplate: getDefaultTemplateForAction(
      MessageActionType.EmailCancellation,
      "email",
    ),
    emailRescheduleTemplate: getDefaultTemplateForAction(
      MessageActionType.EmailReschedule,
      "email",
    ),
    smsConfirmationTemplate: getDefaultTemplateForAction(
      MessageActionType.SMSConfirmation,
      "sms",
    ),
    smsCancellationTemplate: getDefaultTemplateForAction(
      MessageActionType.SMSCancellation,
      "sms",
    ),
    emailReminderTemplate: getDefaultTemplateForAction(
      MessageActionType.EmailReminder,
      "email",
    ),
    smsReminderTemplate: getDefaultTemplateForAction(
      MessageActionType.SMSReminder,
      "sms",
    ),
    smsRescheduleTemplate: getDefaultTemplateForAction(
      MessageActionType.SmsReschedule,
      "sms",
    ),
    preConsultTemplate: null,
    defaultConsultationTemplate: null,
    schedule: {
      monday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
      tuesday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
      wednesday: {
        enabled: true,
        timeBlocks: [{ from: "09:00", to: "17:00" }],
      },
      thursday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
      friday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
      saturday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
      sunday: { enabled: true, timeBlocks: [{ from: "09:00", to: "17:00" }] },
    },
    active: true,
    serviceType: 2,
    serviceCode: 0,
    allowGroupBookings: false,
    maxGroupSize: 0,
    serviceChargesType: 1,
    assignedLocationIds: [],
    assignedPharmacistsByLocation: {},
    scheduleByLocation: {},
  };

  const methods = useForm<Service>({
    defaultValues: initialService
      ? {
          ...defaultService,
          ...initialService,
          serviceChargesType: initialService.serviceChargesType || undefined,
          // For existing services, only set defaults for empty template fields
          emailConfirmationTemplate:
            initialService.emailConfirmationTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.EmailConfirmation,
              "email",
            ),
          emailCancellationTemplate:
            initialService.emailCancellationTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.EmailCancellation,
              "email",
            ),
          emailRescheduleTemplate:
            initialService.emailRescheduleTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.EmailReschedule,
              "email",
            ),
          smsConfirmationTemplate:
            initialService.smsConfirmationTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.SMSConfirmation,
              "sms",
            ),
          smsCancellationTemplate:
            initialService.smsCancellationTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.SMSCancellation,
              "sms",
            ),
          emailReminderTemplate:
            initialService.emailReminderTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.EmailReminder,
              "email",
            ),
          smsReminderTemplate:
            initialService.smsReminderTemplate ??
            getDefaultTemplateForAction(MessageActionType.SMSReminder, "sms"),
          smsRescheduleTemplate:
            initialService.smsRescheduleTemplate ??
            getDefaultTemplateForAction(MessageActionType.SmsReschedule, "sms"),
          assignedLocationIds: initialService.assignedLocationIds || [],
          assignedPharmacistsByLocation:
            initialService.assignedPharmacistsByLocation || {},
          scheduleByLocation: initialService.scheduleByLocation || {},
          assignedDatesForLocations:
    initialService.assignedDatesForLocations || {},
        }
      : defaultService,
    mode: "onBlur",
  });

  const watchedValues = methods.watch();
  const [templates, setTemplate] = useState<Template[]>([]);
  const [services, setServices] = useState<Service[]>([]);

  const [showIframeModal, setShowIframeModal] = useState(false);
  const [iframeCopied, setIframeCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [isChecklistLoading, setIsChecklistLoading] = useState(false);
  const [defaultCompanyChecklist, setDefaultCompanyChecklist] =
    useState<DefaultCompanyChecklist | null>(null);
  const defaultPharmacyServices =
    Array.isArray(pharmacyServicesFromPharmacyState) &&
    pharmacyServicesFromPharmacyState.length > 0
      ? pharmacyServicesFromPharmacyState
      : pharmacyServicesFromMemberState;
  const isPharmacyServiceLoading =
    !!isPharmacyLoadingFromPharmacyState || !!isPharmacyLoadingFromMemberState;

  const mapApiServicesToUi = (apiServices: any[] = []): Service[] =>
    apiServices.map((s: any) => ({
      _id: s?._id || "",
      name: s?.serviceName || "",
      price: parseFloat(s?.serviceCharges ?? ""),
      duration: s?.serviceDuration ?? 0,
      serviceType: s?.serviceType,
      serviceCode: s?.serviceCode,
      color: s?.serviceColor || "#3b82f6",
      showOnPublicCalendar: s?.isOnlineBooking ?? true,
      roomRequired: s?.isRoomRequired ?? false,
      enablePrePayments: false,
      emailConfirmationTemplate: s?.emailConfirmationTemplate || "",
      emailCancellationTemplate: s?.emailCancellationTemplate || "",
      smsConfirmationTemplate: s?.smsConfirmationTemplate || "",
      smsCancellationTemplate: s?.smsCancellationTemplate || "",
      emailReminderTemplate: s?.emailReminderTemplate || "",
      smsReminderTemplate: s?.smsReminderTemplate || "",
      emailRescheduleTemplate: s?.emailRescheduleTemplate || "",
      smsRescheduleTemplate: s?.smsRescheduleTemplate || "",
      preConsultTemplate: s.preConsultationTemplateSection?._id ?? "",
      defaultConsultationTemplate:
        s.defaultConsultationTemplateSection?._id ?? "",
      assignedPharmacistIds: [],
      assignedRoomIds: s?.assignedRoomIds || [],
      assignedLocationIds: s?.assignedLocationIds || [], // ← add
      assignedPharmacistsByLocation: s?.assignedPharmacistsByLocation || {}, // ← add
      scheduleByLocation: s?.scheduleByLocation || {},
     assignedDatesForLocations: Object.entries(s?.assignedDatesForLocations || {}).reduce(
      (acc, [locationId, dates]: [string, any]) => {
        acc[locationId] = {
          serviceStartDate: dates?.serviceStartDate
            ? dates.serviceStartDate.split("T")[0]
            : "",
          serviceEndDate: dates?.serviceEndDate
            ? dates.serviceEndDate.split("T")[0]
            : "",
        };
        return acc;
      },
      {} as Record<string, { serviceStartDate: string; serviceEndDate: string }>
    ),
      schedule: {
        monday: {
          enabled: true,
          timeBlocks: [{ from: "09:00", to: "17:00" }],
        },
        tuesday: {
          enabled: true,
          timeBlocks: [{ from: "09:00", to: "17:00" }],
        },
        wednesday: {
          enabled: true,
          timeBlocks: [{ from: "09:00", to: "17:00" }],
        },
        thursday: {
          enabled: true,
          timeBlocks: [{ from: "09:00", to: "17:00" }],
        },
        friday: {
          enabled: true,
          timeBlocks: [{ from: "09:00", to: "17:00" }],
        },
        saturday: { enabled: false, timeBlocks: [] },
        sunday: { enabled: false, timeBlocks: [] },
      },
      active: true,
      allowGroupBookings: s?.allowGroupBookings,
      maxGroupSize: s?.maxGroupSize,
      serviceChargesType: s?.serviceChargesType || 1,
      sortingNumber: s?.serviceSortingNumber || 0,
    }));

  const refreshServicesFromDispatcher = async () => {
    const normalizedServices = Array.isArray(defaultPharmacyServices)
      ? defaultPharmacyServices
      : defaultPharmacyServices?.response;
    if (Array.isArray(normalizedServices)) {
      setServices(mapApiServicesToUi(normalizedServices));
    }
  };

  const generateIframeCode = () => {
    const baseUrl = `${import.meta.env.VITE_BOOKING_URL}${companyId}`;
    return `<iframe src="${baseUrl}" width="100%" height="600" frameborder="0"></iframe>`;
  };

  const handleCopyIframe = () => {
    navigator.clipboard.writeText(generateIframeCode());
    setIframeCopied(true);
    setTimeout(() => setIframeCopied(false), 2000);
  };

  useEffect(() => {
    const newValues = initialService
      ? {
          ...defaultService,
          ...initialService,
          serviceChargesType: initialService.serviceChargesType || undefined,
          // Always use schedule from initialService if available
          schedule: initialService.schedule || defaultService.schedule,
          // For existing services, only set defaults for empty template fields
          emailConfirmationTemplate:
            initialService.emailConfirmationTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.EmailConfirmation,
              "email",
            ),
          emailCancellationTemplate:
            initialService.emailCancellationTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.EmailCancellation,
              "email",
            ),
          emailRescheduleTemplate:
            initialService.emailRescheduleTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.EmailReschedule,
              "email",
            ),
          smsConfirmationTemplate:
            initialService.smsConfirmationTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.SMSConfirmation,
              "sms",
            ),
          smsCancellationTemplate:
            initialService.smsCancellationTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.SMSCancellation,
              "sms",
            ),
          emailReminderTemplate:
            initialService.emailReminderTemplate ??
            getDefaultTemplateForAction(
              MessageActionType.EmailReminder,
              "email",
            ),
          smsReminderTemplate:
            initialService.smsReminderTemplate ??
            getDefaultTemplateForAction(MessageActionType.SMSReminder, "sms"),
          smsRescheduleTemplate:
            initialService.smsRescheduleTemplate ??
            getDefaultTemplateForAction(MessageActionType.SmsReschedule, "sms"),
          assignedLocationIds: initialService.assignedLocationIds || [],
          assignedPharmacistsByLocation:
            initialService.assignedPharmacistsByLocation || {},
          scheduleByLocation: initialService.scheduleByLocation || {},
          assignedDatesForLocations:
    initialService.assignedDatesForLocations || {},
        }
      : defaultService;
    methods.reset(newValues);
  }, [
    initialService?._id,
    JSON.stringify(initialService?.schedule),
    initialService?.name,
  ]);

  useEffect(() => {
    // Combine both emailsAndSmsTemplates and defaultCompanyMessageTemplates
    const allTemplates = [];

    // Add regular templates
    if (emailsAndSmsTemplates && Array.isArray(emailsAndSmsTemplates)) {
      allTemplates.push(...emailsAndSmsTemplates);
    }

    // Add default company message templates
    if (
      defaultCompanyMessageTemplates &&
      Array.isArray(defaultCompanyMessageTemplates)
    ) {
      allTemplates.push(...defaultCompanyMessageTemplates);
    }

    if (allTemplates.length > 0) {
      setTemplate(
        allTemplates.map((s: any) => ({
          id: s._id || "",
          name: s?.templateName || "",
          type: s?.messageType || "",
          subject: s?.subject || "",
          content: s?.text || "",
          actionType: s?.actionType || "",
          isDefault: !!s.isDefault, // Add this flag to identify default templates
        })),
      );
    }
  }, [emailsAndSmsTemplates, defaultCompanyMessageTemplates]);

  useEffect(() => {
    if (defaultPharmacyServices && Array.isArray(defaultPharmacyServices)) {
      setServices(mapApiServicesToUi(defaultPharmacyServices));
    }
  }, [defaultPharmacyServices]);

  const dayNames = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  const dayLabels = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const getDayName = (dayOfWeek: number): keyof ServiceSchedule => {
    const dayMap: { [key: number]: keyof ServiceSchedule } = {
      1: "monday",
      2: "tuesday",
      3: "wednesday",
      4: "thursday",
      5: "friday",
      6: "saturday",
      0: "sunday",
      7: "sunday",
    };
    return dayMap[dayOfWeek] || "monday";
  };

  const transformApiDataToService = (
    apiData: any,
    originalService: Service,
    data: any,
  ): Service => {
    const updatedSchedule: ServiceSchedule = {
      monday: { enabled: false, timeBlocks: [] },
      tuesday: { enabled: false, timeBlocks: [] },
      wednesday: { enabled: false, timeBlocks: [] },
      thursday: { enabled: false, timeBlocks: [] },
      friday: { enabled: false, timeBlocks: [] },
      saturday: { enabled: false, timeBlocks: [] },
      sunday: { enabled: false, timeBlocks: [] },
    };

    // Use API response availability (fresh from DB), NOT Redux availability (which is stale)
    const apiAvailability = apiData?.availability || [];
    if (apiAvailability && Array.isArray(apiAvailability)) {
      apiAvailability.forEach((dayAvailability: any) => {
        const dayName = getDayName(dayAvailability?.dayOfWeek);

        if (dayAvailability.slots && dayAvailability?.slots?.length > 0) {
          updatedSchedule[dayName] = {
            enabled: true,
            timeBlocks: dayAvailability?.slots?.map((slot: any) => ({
              from: slot?.startTime,
              to: slot?.endTime,
            })),
          };
        }
      });
    }

    const updatedService: Service = {
      ...originalService,
      sortingNumber: originalService?.sortingNumber,
      showOnPublicCalendar:
        apiData?.detail?.isPublicCalendar ??
        originalService?.showOnPublicCalendar,
      roomRequired:
        apiData?.detail?.isRoomRequired ?? originalService?.roomRequired,
      duration:
        parseInt(apiData?.appointmentOptions?.duration) ||
        originalService?.duration,
      color: apiData?.appointmentOptions?.colour || originalService?.color,
      enablePrePayments:
        apiData?.appointmentOptions?.isEnablePrePayment ??
        originalService?.enablePrePayments,
      emailConfirmationTemplate:
        apiData?.confirmationCancellation?.emailConfirmation ||
        originalService?.emailConfirmationTemplate,
      emailCancellationTemplate:
        apiData?.confirmationCancellation?.emailCancellation ||
        originalService?.emailCancellationTemplate,
      smsConfirmationTemplate:
        apiData?.confirmationCancellation?.smsConfirmation ||
        originalService?.smsConfirmationTemplate,
      smsCancellationTemplate:
        apiData?.confirmationCancellation?.smsCancellation ||
        originalService?.smsCancellationTemplate,
      emailReminderTemplate:
        apiData?.reminders?.emailReminder ||
        originalService?.emailReminderTemplate,
      smsReminderTemplate:
        apiData?.reminders?.smsReminder || originalService?.smsReminderTemplate,
      emailRescheduleTemplate:
        apiData?.reschedule?.emailReschedule ||
        originalService?.emailRescheduleTemplate,
      smsRescheduleTemplate:
        apiData?.reschedule?.smsReschedule ||
        originalService?.smsRescheduleTemplate,
      assignedPharmacistIds: data?.assignedPharmacistIds,
      assignedRoomIds: data?.assignedRoomIds,
      allowGroupBookings: originalService?.allowGroupBookings,
      maxGroupSize: originalService?.maxGroupSize,
      schedule: updatedSchedule,
    };

    return updatedService;
  };

  const getServiceData = async (service: any) => {
    try {
      const data = await getClinicalServiceBuilderResponse(
        service?._id,
        companyId,
      );
      const updatedService = transformApiDataToService(
        data?.clinicalServiceBuilder?.product,
        service,
        data,
      );

      // If service already has per-location data saved, use it directly
      if ((service.assignedLocationIds?.length ?? 0) > 0) {
        return {
          ...updatedService,
          assignedLocationIds: service.assignedLocationIds || [],
          assignedPharmacistsByLocation:
            service.assignedPharmacistsByLocation || {},
          scheduleByLocation: service.scheduleByLocation || {},
          assignedRoomIds:
            data?.assignedRoomIds || service.assignedRoomIds || [],
        };
      }

      // Legacy fallback: derive from rooms/members for older services
      const locationAssignments = buildLocationAssignments(
        service._id,
        allPharmacies || [],
        membersData || [],
        updatedService.schedule,
      );

      return { ...updatedService, ...locationAssignments };
    } catch (error) {
      console.error("Error while editing service:", error);
    }
  };
  const handleDeleteService = async (serviceID) => {
    try {
      const result = await deleteNewService({
        serviceID,
        pharmacyID: currentPharmacyId,
      });
      if (result !== true) {
        showError(
          "This service cannot be deleted because it has upcoming appointments.",
        );
        return;
      }
      const freshServices = await getDefaultCompanyServices(companyId);
      const normalizedServices = Array.isArray(freshServices)
        ? freshServices
        : freshServices?.response;
      if (Array.isArray(normalizedServices)) {
        setServices(mapApiServicesToUi(normalizedServices));
      }
    } catch (error) {
      console.error("Error while deleting service:", error);
    }
  };

  const handleEditService = async (service: Service, onEditService) => {
    try {
      const serviceData = await getServiceData(service);
      if (!serviceData) {
        onEditService(service);
        return;
      }
      onEditService(serviceData);
    } catch (error) {
      console.error("Error while editing service:", error);
    }
  };

   const handleDuplicateService = async (serviceId: string) => {
    try{
      const isServiceDuplicated = await duplicateService(serviceId,companyId);
      if(!isServiceDuplicated) return;
      await getDefaultCompanyServices(companyId);
    }
    catch(ex){
    console.error("Error while duplicating service:", ex);
    }
  }

  const handleAddService = async (onAddService) => {
    try {
      await getEmailsAndSmsTemplates(companyId);
      onAddService();
    } catch (error) {
      console.error("Error while editing service:", error);
    }
  };

  const handleUpdateSchedule = (
    day: keyof ServiceSchedule,
    field: keyof DaySchedule,
    value: any,
  ) => {
    const currentSchedule = methods.getValues("schedule");

    let updatedDay = {
      ...currentSchedule[day],
      [field]: value,
    };
    if (field === "enabled" && value === false) {
      updatedDay.timeBlocks = [];
    }
    if (
      field === "enabled" &&
      value === true &&
      updatedDay.timeBlocks.length === 0
    ) {
      updatedDay.timeBlocks = [{ from: "09:00", to: "17:00" }];
    }

    methods.setValue(
      "schedule",
      {
        ...currentSchedule,
        [day]: updatedDay,
      },
      { shouldDirty: true },
    );
  };

  const handleAddTimeBlock = (day: keyof ServiceSchedule) => {
    const currentSchedule = methods.getValues("schedule");
    methods.setValue(
      "schedule",
      {
        ...currentSchedule,
        [day]: {
          ...currentSchedule[day],
          timeBlocks: [
            ...currentSchedule[day].timeBlocks,
            { from: "09:00", to: "17:00" },
          ],
        },
      },
      { shouldDirty: true },
    );
  };

  const handleGetAllRooms = () => {
    const rooms: {
      id: string;
      name: string;
      locationId: string;
    }[] = [];
    roomsData.forEach((room) => {
      rooms.push({
        id: room._id,
        name: room.roomName,
        locationId: room.pharmacyID,
      });
    });
    return rooms;
  };

  const getRoomById = (roomId: string) => {
    return roomsData.find((room) => room._id === roomId);
  };

  const convertRoomAvailabilityToSchedule = (
    roomData: any,
  ): ServiceSchedule => {
    const defaultSchedule: ServiceSchedule = {
      monday: { enabled: false, timeBlocks: [] },
      tuesday: { enabled: false, timeBlocks: [] },
      wednesday: { enabled: false, timeBlocks: [] },
      thursday: { enabled: false, timeBlocks: [] },
      friday: { enabled: false, timeBlocks: [] },
      saturday: { enabled: false, timeBlocks: [] },
      sunday: { enabled: false, timeBlocks: [] },
    };

    if (!roomData || !roomData.availability) {
      return defaultSchedule;
    }

    roomData.availability.forEach((dayAvailability: any) => {
      const dayName = getDayName(dayAvailability.dayOfWeek);
      if (dayAvailability.slots && dayAvailability.slots.length > 0) {
        defaultSchedule[dayName] = {
          enabled: true,
          timeBlocks: dayAvailability.slots.map((slot: any) => ({
            from: slot.startTime,
            to: slot.endTime,
          })),
        };
      }
    });

    return defaultSchedule;
  };

  const handleRemoveTimeBlock = (day: keyof ServiceSchedule, index: number) => {
    const currentSchedule = methods.getValues("schedule");
    methods.setValue(
      "schedule",
      {
        ...currentSchedule,
        [day]: {
          ...currentSchedule[day],
          timeBlocks: currentSchedule[day].timeBlocks.filter(
            (_, i) => i !== index,
          ),
        },
      },
      { shouldDirty: true },
    );
  };

  const handleUpdateTimeBlock = (
    day: keyof ServiceSchedule,
    index: number,
    field: keyof TimeBlock,
    value: string,
  ) => {
    const currentSchedule = methods.getValues("schedule");
    methods.setValue(
      "schedule",
      {
        ...currentSchedule,
        [day]: {
          ...currentSchedule[day],
          timeBlocks: currentSchedule[day].timeBlocks.map((block, i) =>
            i === index ? { ...block, [field]: value } : block,
          ),
        },
      },
      { shouldDirty: true },
    );
  };

  const onSubmit = async (data: Service) => {
    // Prevent multiple submissions
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const assignedLocationIds = data.assignedLocationIds || [];
      const scheduleByLocation = data.scheduleByLocation || {};
      const cleanedScheduleByLocation = Object.keys(scheduleByLocation).reduce(
        (acc: { [locId: string]: ServiceSchedule }, locId) => {
          if (assignedLocationIds.includes(locId)) {
            acc[locId] = scheduleByLocation[locId];
          }
          return acc;
        },
        {},
      );
      const assignedDatesForLocations =
      Object.entries(data.assignedDatesForLocations || {}).reduce(
        (acc, [locationId, dates]) => {
          acc[locationId] = {
            serviceStartDate: dates.serviceStartDate
              ? new Date(dates.serviceStartDate)
                  .toISOString()
                  .replace("Z", "+00:00")
              : null,

            serviceEndDate: dates.serviceEndDate
              ? new Date(dates.serviceEndDate)
                  .toISOString()
                  .replace("Z", "+00:00")
              : null,
          };

          return acc;
        },
        {} as Record<
          string,
          {
            serviceStartDate: string | null;
            serviceEndDate: string | null;
          }
        >
      );

      const locKeys = Object.keys(cleanedScheduleByLocation);
      const derivedSchedule =
        locKeys.length > 0
          ? cleanedScheduleByLocation[locKeys[0]]
          : data.schedule;
      const { assignedPharmacistIds, ...restData } = data;

      const payload = {
        ...restData,
        price: String(data.price),
        serviceChargesType: data.serviceChargesType || 1,
        assignedRoomIds: data.assignedRoomIds || [],
        // assignedPharmacistIds: data.assignedPharmacistIds || [],
        assignedLocationIds,
        assignedPharmacistsByLocation: data.assignedPharmacistsByLocation || {},
        scheduleByLocation: cleanedScheduleByLocation,
        schedule: derivedSchedule,
        serviceSortingNumber: initialService?.sortingNumber ?? 0,
        companyID: companyId,
        pharmacyID: currentPharmacyId,
        assignedDatesForLocations:assignedDatesForLocations
      };
      if (initialService?._id) {
        await updateNewServiceType(payload);
        await refreshServicesFromDispatcher();

        const updatedService = {
          ...initialService,
          ...data,
          schedule: watchedValues.schedule,
          sortingNumber: initialService?.sortingNumber,
        };

        if (onSave) {
          onSave(updatedService);
        }
      } else {
        await addNewServiceType(payload);
        trackFirstTimeIntercomEvent({
          eventName: "service_created_first_time",
          user: {
            id: currentUser.emailAddress,
            email: currentUser.emailAddress,
            name: currentUser.fullName,
          },
          properties: {
            source: "service_settings",
          },
        });
        await refreshServicesFromDispatcher();
      }

      onBack();
    } catch (error) {
      console.error("Error saving service:", error);
      // Optionally show error toast here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublicUrlClick = async () => {
    setIsChecklistLoading(true);
    try {
      const checklistResponse = await getDefaultCompanyCheckList(companyId);
      setDefaultCompanyChecklist(checklistResponse || null);
      setShowChecklistModal(true);
    } finally {
      setIsChecklistLoading(false);
    }
  };

  const handleGetServiceQRCheckList = async (serviceId: string) => {
    let result = null;
    try {
      result = await getQRServiceCheckList(
        companyId,
        serviceId,
        currentPharmacyId,
      );
    } catch (error) {
      console.error("Error getting service QR checklist:", error);
      result = null;
    }
    return result;
  };

  const getRoomsByPharmacyId = (pharmacyId: string) => {
    return (roomsData || []).filter(
      (room: any) => room.pharmacyID === pharmacyId,
    );
  };

  const buildLocationAssignments = (
    serviceId: string,
    allPharmacies: any[],
    membersData: any[],
    serviceSchedule: ServiceSchedule, // ← already a ServiceSchedule, no conversion needed
  ) => {
    const assignedRoomIds: string[] = [];
    const assignedLocationIds: string[] = [];
    const assignedPharmacistsByLocation: { [locId: string]: string[] } = {};
    const scheduleByLocation: { [locId: string]: ServiceSchedule } = {};

    (allPharmacies || []).forEach((pharmacy: any) => {
      const locId = pharmacy.pharmacyID;
      const rooms = pharmacy.pharmacyRooms || [];

      // Rooms: check if this service is in the room's services array
      const assignedRoomsForLoc: string[] = [];
      rooms.forEach((room: any) => {
        const isAssigned = (room.services || []).some(
          (s: any) => s._id === serviceId,
        );
        if (isAssigned && !assignedRoomIds.includes(room._id)) {
          assignedRoomsForLoc.push(room._id);
          assignedRoomIds.push(room._id);
        }
      });

      // Pharmacists: member must have this service AND this pharmacy
      const assignedPharmacistsForLoc: string[] = [];
      (membersData || []).forEach((member: any) => {
        const hasService = (member.services || []).some(
          (s: any) => s._id === serviceId,
        );
        const hasPharmacy = (member.pharmacies || []).some(
          (p: any) => p.pharmacyID === locId,
        );
        if (hasService && hasPharmacy) {
          assignedPharmacistsForLoc.push(member.emailAddress);
        }
      });

      if (assignedPharmacistsForLoc.length > 0) {
        assignedPharmacistsByLocation[locId] = assignedPharmacistsForLoc;
      }

      // Mark location as configured if it has any rooms or pharmacists
      if (
        assignedRoomsForLoc.length > 0 ||
        assignedPharmacistsForLoc.length > 0
      ) {
        assignedLocationIds.push(locId);
        scheduleByLocation[locId] = serviceSchedule; // ← use directly, already correct format
      }
    });

    return {
      assignedLocationIds,
      assignedRoomIds,
      assignedPharmacistsByLocation,
      scheduleByLocation,
    };
  };

  return {
    // ServicesSettingsTab
    services,
    isDarkMode,
    isPharmacyServiceLoading,
    methods,
    watchedValues,
    currentAiTemplates,
    isAhtWeightManagementInstalled,
    currentPreConsultTemplates,
    showChecklistModal,
    isChecklistLoading,
    defaultCompanyChecklist,
    allPharmacies: (allPharmacies || []).filter((p: any) => p.active !== false),
    handleAddService,
    handleEditService,
    handleDuplicateService,
    handleDeleteService,
    handlePublicUrlClick,
    handleGetServiceQRCheckList,
    setShowChecklistModal,
    getRoomsByPharmacyId,
    removeServiceFromPharmacy,
    // ServiceFormPanel
    dayLabels,
    dayNames,
    membersData,
    roomsData,
    handleGetAllRooms,
    getRoomById,
    convertRoomAvailabilityToSchedule,
    handleAddTimeBlock,
    handleUpdateSchedule,
    handleRemoveTimeBlock,
    handleUpdateTimeBlock,
    onSubmit,
    isSubmitting,
    // TemplateSelector
    templates,
    defaultCompanyMessageTemplates,

    //iFrame
    showIframeModal,
    setShowIframeModal,
    iframeCopied,
    setIframeCopied,
    generateIframeCode,
    handleCopyIframe,
    companyId,
    updateServiceSorting,
    refreshServicesFromDispatcher,
  };
};
